import { Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"
import { useEffect, useMemo, useState } from "react"
import type { FiscalSettingRates } from "../../lib/invoiceTributos"
import {
  computeInvoiceTributosBreakdown,
  matchFiscalSettingForMunicipio
} from "../../lib/invoiceTributos"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"

export type ClientOption = {
  id: number
  company_id: number | null
  name: string
  municipio: string
  cep_tomador?: string
  codigo_sap: string
  cr: string
  omie_n_cod_cli?: number | null
  cadastro_minimo_omie?: boolean
  sem_configuracao_fiscal?: boolean
  municipios: string[]
  fiscal_settings: FiscalSettingRates[]
}

export type CompanyOption = {
  id: number
  name: string
  cnpj: string
  omie_servicos: { id: number; label: string }[]
  omie_contas_correntes: { id: number; label: string }[]
}

export type PaymentTermOption = {
  id: number
  label: string
  name: string
  days: number
}

type InvoiceFormData = {
  id?: number
  codigo_sap: string
  cr: string
  pedido_compras: string
  company_id: number | null
  client_id: number | null
  municipio: string
  /** Código IBGE do município da prestação (7 dígitos), ex. 3550308 — enviado à Omie em DetalhesNfse.cCidadeEvento */
  ibge_codigo_municipio_prestacao?: string
  medicao: string
  periodo_inicio: string
  periodo_fim: string
  valor: string
  data_vencimento: string
  omie_servico_id?: number | null
  omie_conta_corrente_id?: number | null
  payment_term_id?: number | null
}

type InvoiceFormProps = {
  initialValues: InvoiceFormData
  companies: CompanyOption[]
  clients: ClientOption[]
  paymentTerms?: PaymentTermOption[]
  submitUrl: string
  submitLabel: string
  method: "post" | "patch"
  errors?: Record<string, string[]>
}

function toNumber(value: string) {
  const parsed = Number(value.replace(",", "."))
  return Number.isFinite(parsed) ? parsed : 0
}

function currency(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value)
}

function pct(value: number) {
  return `${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%`
}

function ErrorText({ value }: { value?: string[] }) {
  if (!value?.length) return null
  return <p className="text-xs text-rose-400">{value[0]}</p>
}

const CLIENT_SEARCH_LIMIT = 80

export function InvoiceForm({ initialValues, companies, clients, paymentTerms, submitUrl, submitLabel, method, errors }: InvoiceFormProps) {
  const [clientSearch, setClientSearch] = useState("")
  const [ibgeLookupCep, setIbgeLookupCep] = useState("")
  const [ibgeLookupStatus, setIbgeLookupStatus] = useState("")

  const form = useForm({
    invoice: {
      codigo_sap: initialValues.codigo_sap ?? "",
      cr: initialValues.cr ?? "",
      pedido_compras: initialValues.pedido_compras ?? "",
      data_vencimento: initialValues.data_vencimento ?? "",
      company_id: initialValues.company_id ? String(initialValues.company_id) : "",
      client_id: initialValues.client_id ? String(initialValues.client_id) : "",
      municipio: initialValues.municipio ?? "",
      ibge_codigo_municipio_prestacao: initialValues.ibge_codigo_municipio_prestacao ?? "",
      medicao: initialValues.medicao ?? "",
      periodo_inicio: initialValues.periodo_inicio ?? "",
      periodo_fim: initialValues.periodo_fim ?? "",
      valor: initialValues.valor ?? "",
      omie_servico_id: initialValues.omie_servico_id != null ? String(initialValues.omie_servico_id) : "",
      omie_conta_corrente_id: initialValues.omie_conta_corrente_id != null ? String(initialValues.omie_conta_corrente_id) : "",
      payment_term_id: initialValues.payment_term_id != null ? String(initialValues.payment_term_id) : ""
    }
  })

  const inv = form.data.invoice

  const clientsDaEmpresa = useMemo(() => {
    const cid = inv.company_id?.trim()
    if (!cid) return []
    return clients.filter((c) => c.company_id != null && String(c.company_id) === cid)
  }, [clients, inv.company_id])

  const clientesFiltradosBusca = useMemo(() => {
    const q = clientSearch.trim().toLowerCase()
    if (!q) return clientsDaEmpresa
    return clientsDaEmpresa.filter((c) => {
      const hay = [
        c.name,
        c.codigo_sap,
        c.cr,
        c.municipio,
        ...(c.municipios ?? [])
      ]
        .join(" ")
        .toLowerCase()
      return hay.includes(q) || hay.split(/\s+/).some((w) => w.startsWith(q))
    })
  }, [clientsDaEmpresa, clientSearch])

  const selectedClient = useMemo(
    () => clients.find((client) => String(client.id) === inv.client_id),
    [clients, inv.client_id]
  )

  const selectedClientValidoParaEmpresa = useMemo(() => {
    if (!selectedClient || !inv.company_id?.trim()) return false
    return (
      selectedClient.company_id != null && String(selectedClient.company_id) === inv.company_id.trim()
    )
  }, [selectedClient, inv.company_id])

  const selectedCompany = useMemo(
    () => companies.find((c) => String(c.id) === inv.company_id),
    [companies, inv.company_id]
  )

  useEffect(() => {
    if (!selectedClientValidoParaEmpresa || !selectedClient) return
    setIbgeLookupCep((selectedClient.cep_tomador ?? "").replace(/\D/g, "").slice(0, 8))
  }, [selectedClientValidoParaEmpresa, selectedClient])

  useEffect(() => {
    if (!inv.company_id?.trim() || !inv.client_id?.trim()) return
    const c = clients.find((cl) => String(cl.id) === inv.client_id)
    if (!c || c.company_id == null || String(c.company_id) !== inv.company_id.trim()) {
      form.setData("invoice", {
        ...inv,
        client_id: "",
        codigo_sap: "",
        cr: "",
        municipio: "",
        ibge_codigo_municipio_prestacao: "",
        omie_servico_id: "",
        omie_conta_corrente_id: ""
      })
      setClientSearch("")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- evita loop; só reage a empresa/cliente na lista
  }, [inv.company_id, inv.client_id, clients])

  const municipiosDoCliente = useMemo(() => {
    const base = [...(selectedClient?.municipios ?? [])]
    const atual = inv.municipio?.trim()
    if (atual && !base.includes(atual)) base.push(atual)
    return base.sort((a, b) => a.localeCompare(b, "pt-BR"))
  }, [selectedClient?.municipios, inv.municipio])

  const temCidadesFiscais = (selectedClient?.municipios?.length ?? 0) > 0

  const fiscalSetting = useMemo(
    () => matchFiscalSettingForMunicipio(selectedClient?.fiscal_settings, inv.municipio ?? "", inv.company_id),
    [selectedClient?.fiscal_settings, inv.municipio, inv.company_id]
  )

  const servicosOmie = selectedCompany?.omie_servicos ?? []
  const contasOmie = selectedCompany?.omie_contas_correntes ?? []

  useEffect(() => {
    const company = companies.find((c) => String(c.id) === inv.company_id)
    const client = clients.find((cl) => String(cl.id) === inv.client_id)
    const muni = inv.municipio?.trim()
    if (!company?.id || !client?.id || !muni) {
      if (inv.omie_servico_id || inv.omie_conta_corrente_id) {
        form.setData("invoice", { ...inv, omie_servico_id: "", omie_conta_corrente_id: "" })
      }
      return
    }

    const fiscal = matchFiscalSettingForMunicipio(client.fiscal_settings, muni, inv.company_id)
    const sList = company.omie_servicos
    const cList = company.omie_contas_correntes
    const defS = fiscal?.omie_servico_id != null ? String(fiscal.omie_servico_id) : ""
    const defC = fiscal?.omie_conta_corrente_id != null ? String(fiscal.omie_conta_corrente_id) : ""

    let nextS = inv.omie_servico_id || ""
    if (!nextS || !sList.some((x) => String(x.id) === nextS)) {
      if (sList.length === 1) nextS = String(sList[0].id)
      else if (defS && sList.some((x) => String(x.id) === defS)) nextS = defS
      else nextS = ""
    }

    let nextC = inv.omie_conta_corrente_id || ""
    if (!nextC || !cList.some((x) => String(x.id) === nextC)) {
      if (cList.length === 1) nextC = String(cList[0].id)
      else if (defC && cList.some((x) => String(x.id) === defC)) nextC = defC
      else nextC = ""
    }

    if (nextS !== inv.omie_servico_id || nextC !== inv.omie_conta_corrente_id) {
      form.setData("invoice", { ...inv, omie_servico_id: nextS, omie_conta_corrente_id: nextC })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omie ids ajustados só quando empresa/cliente/município mudam
  }, [inv.company_id, inv.client_id, inv.municipio, companies, clients])

  const valorNum = toNumber(inv.valor)
  const tributosBreakdown = useMemo(
    () => computeInvoiceTributosBreakdown(valorNum, fiscalSetting),
    [valorNum, fiscalSetting]
  )
  const tributosCalculados = tributosBreakdown.total
  const totalDaNota = Math.round((valorNum + tributosCalculados) * 100) / 100

  const semConfigFiscalParaMunicipio =
    Boolean(inv.client_id?.trim()) && Boolean(inv.municipio?.trim()) && fiscalSetting === undefined

  const alertasClienteOmie =
    selectedClient?.cadastro_minimo_omie === true || selectedClient?.sem_configuracao_fiscal === true

  function onClientChange(clientId: string) {
    const c = clients.find((cl) => String(cl.id) === clientId)
    const sap = c?.codigo_sap?.trim() ? c.codigo_sap.trim() : ""
    const cr = c?.cr?.trim() ? c.cr.trim() : ""
    form.setData("invoice", {
      ...inv,
      client_id: clientId,
      codigo_sap: sap,
      cr: cr,
      municipio: "",
      ibge_codigo_municipio_prestacao: ""
    })
    setIbgeLookupCep((c?.cep_tomador ?? "").replace(/\D/g, "").slice(0, 8))
    setIbgeLookupStatus("")
    setClientSearch("")
  }

  async function buscarIbgeViaCep() {
    const cep = ibgeLookupCep.replace(/\D/g, "")
    if (!cep.match(/^\d{8}$/)) {
      setIbgeLookupStatus("Informe um CEP válido com 8 dígitos.")
      return
    }

    setIbgeLookupStatus("Buscando IBGE no ViaCEP...")
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
      if (!res.ok) {
        setIbgeLookupStatus("Não foi possível consultar o ViaCEP agora.")
        return
      }
      const data = (await res.json()) as { erro?: boolean; ibge?: string }
      if (data.erro) {
        setIbgeLookupStatus("CEP não encontrado no ViaCEP.")
        return
      }
      const ibge = (data.ibge ?? "").replace(/\D/g, "")
      if (!ibge.match(/^\d{7}$/)) {
        setIbgeLookupStatus("ViaCEP não retornou código IBGE válido para este CEP.")
        return
      }

      form.setData("invoice", { ...inv, ibge_codigo_municipio_prestacao: ibge })
      setIbgeLookupStatus(`IBGE preenchido automaticamente: ${ibge}.`)
    } catch {
      setIbgeLookupStatus("Erro ao consultar o ViaCEP.")
    }
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (method === "patch") {
      form.patch(submitUrl)
      return
    }
    form.post(submitUrl)
  }

  return (
    <form onSubmit={submit} className="space-y-6 rounded-xl border border-slate-800 bg-slate-900 p-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="company_id">Empresa</Label>
          <select
            id="company_id"
            required
            value={inv.company_id}
            onChange={(e) => {
              form.setData("invoice", {
                ...inv,
                company_id: e.target.value,
                client_id: "",
                codigo_sap: "",
                cr: "",
                municipio: "",
                ibge_codigo_municipio_prestacao: "",
                omie_servico_id: "",
                omie_conta_corrente_id: ""
              })
              setClientSearch("")
            }}
            className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
          >
            <option value="">Selecione a empresa</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name} ({company.cnpj})
              </option>
            ))}
          </select>
          <ErrorText value={errors?.company_id} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="client_search">Cliente</Label>
          {!inv.company_id?.trim() ? (
            <p className="rounded-md border border-slate-700 bg-slate-950/80 px-3 py-2 text-sm text-slate-500">
              Selecione a empresa para listar e buscar clientes.
            </p>
          ) : selectedClientValidoParaEmpresa && selectedClient ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="min-h-10 flex-1 rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100">
                <span className="font-medium">{selectedClient.name}</span>
                {selectedClient.codigo_sap?.trim() ? (
                  <span className="ml-2 text-slate-400">SAP {selectedClient.codigo_sap}</span>
                ) : null}
              </div>
              <Button
                type="button"
                variant="outline"
                className="shrink-0 border-slate-600"
                onClick={() => {
                  onClientChange("")
                  setClientSearch("")
                }}
              >
                Trocar cliente
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Input
                id="client_search"
                autoComplete="off"
                placeholder="Buscar por nome, SAP, CR ou município…"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                className="bg-slate-950"
              />
              <div className="max-h-52 overflow-y-auto rounded-md border border-slate-700 bg-slate-950">
                {clientsDaEmpresa.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-slate-500">
                    Nenhum cliente vinculado a esta empresa. Cadastre o cliente com a empresa correta ou sincronize pela
                    Omie.
                  </p>
                ) : clientesFiltradosBusca.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-slate-500">Nenhum resultado para “{clientSearch.trim()}”.</p>
                ) : (
                  <ul className="divide-y divide-slate-800" role="listbox">
                    {clientesFiltradosBusca.slice(0, CLIENT_SEARCH_LIMIT).map((client) => {
                      const sufixos: string[] = []
                      if (client.sem_configuracao_fiscal) sufixos.push("sem config. fiscal")
                      if (client.cadastro_minimo_omie) sufixos.push("cadastro incompleto (Omie)")
                      const sub = [client.municipio, client.codigo_sap?.trim() ? `SAP ${client.codigo_sap}` : ""]
                        .filter(Boolean)
                        .join(" · ")
                      return (
                        <li key={client.id}>
                          <button
                            type="button"
                            role="option"
                            className="w-full px-3 py-2 text-left text-sm text-slate-100 hover:bg-slate-800/90"
                            onClick={() => onClientChange(String(client.id))}
                          >
                            <span className="font-medium">
                              {sufixos.length ? `${client.name} — ${sufixos.join(" · ")}` : client.name}
                            </span>
                            {sub ? <div className="text-xs text-slate-500">{sub}</div> : null}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}
                {clientesFiltradosBusca.length > CLIENT_SEARCH_LIMIT ? (
                  <p className="border-t border-slate-800 px-3 py-2 text-xs text-slate-500">
                    Mostrando os primeiros {CLIENT_SEARCH_LIMIT} resultados. Refine a busca para ver outros.
                  </p>
                ) : null}
              </div>
            </div>
          )}
          <ErrorText value={errors?.client_id} />
          {selectedClientValidoParaEmpresa && selectedClient && alertasClienteOmie && (
            <div className="rounded-md border border-amber-800/80 bg-amber-950/25 px-3 py-2 text-xs text-amber-100/95">
              <p className="font-medium text-amber-200">Atenção — cliente precisa de cadastro no portal</p>
              <ul className="mt-1 list-inside list-disc space-y-0.5 text-amber-100/85">
                {selectedClient.sem_configuracao_fiscal && (
                  <li>
                    Sem configuração fiscal: cadastre municípios e tributos em{" "}
                    <a
                      href={`/clientes/${selectedClient.id}/configuracoes-fiscais`}
                      className="underline decoration-amber-400/80 hover:text-amber-50"
                    >
                      Configurações fiscais do cliente
                    </a>
                    .
                  </li>
                )}
                {selectedClient.cadastro_minimo_omie && (
                  <li>
                    Cadastro mínimo vindo da Omie (endereço incompleto): edite o cliente e complete endereço/UF/CEP antes de
                    emitir.
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="municipio">Município (prestação)</Label>
          {inv.client_id && temCidadesFiscais ? (
            <select
              id="municipio"
              required
              value={inv.municipio}
              onChange={(e) => form.setData("invoice", { ...inv, municipio: e.target.value })}
              className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
            >
              <option value="">Selecione o município</option>
              {municipiosDoCliente.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          ) : (
            <>
              <Input
                id="municipio"
                required
                placeholder={
                  inv.client_id
                    ? "Nenhuma config. fiscal com município — informe o município da prestação"
                    : "Selecione um cliente primeiro"
                }
                disabled={!inv.client_id}
                value={inv.municipio}
                onChange={(e) => form.setData("invoice", { ...inv, municipio: e.target.value })}
              />
              {inv.client_id && !temCidadesFiscais && (
                <p className="text-xs text-amber-500/90">
                  Este cliente não tem municípios cadastrados em Config. fiscal. Preencha o município manualmente ou cadastre
                  as configurações fiscais.
                </p>
              )}
            </>
          )}
          <ErrorText value={errors?.municipio} />
          {semConfigFiscalParaMunicipio && (
            <p className="text-xs text-amber-500/90">
              Não há configuração fiscal cadastrada para este cliente neste município (e empresa, se informada na config.).
              O envio será recusado até cadastrar em Configurações fiscais do cliente.
            </p>
          )}
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="ibge_codigo_municipio_prestacao">Código IBGE do município (prestação)</Label>
          <Input
            id="ibge_codigo_municipio_prestacao"
            inputMode="numeric"
            autoComplete="off"
            maxLength={14}
            placeholder="Ex.: 3550308 (7 dígitos) — obrigatório para enviar cCidadeEvento à Omie"
            disabled={!inv.client_id}
            value={inv.ibge_codigo_municipio_prestacao ?? ""}
            onChange={(e) =>
              form.setData("invoice", { ...inv, ibge_codigo_municipio_prestacao: e.target.value.replace(/\D/g, "").slice(0, 7) })
            }
          />
          <p className="text-xs text-slate-500">
            Usado em <span className="text-slate-400">InformacoesAdicionais.DetalhesNfse.Eventos[].cCidadeEvento</span> na API
            IncluirOS. O campo “Município (prestação)” acima continua indo em <span className="text-slate-400">cCidPrestServ</span>{" "}
            (texto).
          </p>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              id="cep_para_ibge"
              inputMode="numeric"
              autoComplete="off"
              maxLength={8}
              placeholder="Digite o CEP para buscar IBGE no ViaCEP"
              value={ibgeLookupCep}
              onChange={(e) => setIbgeLookupCep(e.target.value.replace(/\D/g, "").slice(0, 8))}
              className="sm:max-w-xs"
            />
            <Button type="button" variant="outline" className="border-slate-700" onClick={buscarIbgeViaCep}>
              Buscar IBGE por CEP
            </Button>
          </div>
          {ibgeLookupStatus ? <p className="text-xs text-slate-400">{ibgeLookupStatus}</p> : null}
          <ErrorText value={errors?.ibge_codigo_municipio_prestacao} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="codigo_sap">Código SAP</Label>
          <Input
            id="codigo_sap"
            value={inv.codigo_sap}
            placeholder="Preenchido do cliente se houver; senão digite aqui"
            onChange={(e) => form.setData("invoice", { ...inv, codigo_sap: e.target.value })}
          />
          <ErrorText value={errors?.codigo_sap} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cr">CR</Label>
          <Input
            id="cr"
            value={inv.cr}
            placeholder="Preenchido do cliente se houver; senão digite aqui"
            onChange={(e) => form.setData("invoice", { ...inv, cr: e.target.value })}
          />
          <ErrorText value={errors?.cr} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pedido_compras">Pedido de Compras</Label>
          <Input
            id="pedido_compras"
            value={inv.pedido_compras}
            placeholder="Ex.: 4501757066/5108744416"
            onChange={(e) => form.setData("invoice", { ...inv, pedido_compras: e.target.value })}
          />
          <ErrorText value={errors?.pedido_compras} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="medicao">Medição</Label>
          <Input id="medicao" type="number" step="0.01" value={inv.medicao} onChange={(e) => form.setData("invoice", { ...inv, medicao: e.target.value })} />
          <ErrorText value={errors?.medicao} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="periodo_inicio">Período início</Label>
          <Input id="periodo_inicio" type="date" value={inv.periodo_inicio} onChange={(e) => form.setData("invoice", { ...inv, periodo_inicio: e.target.value })} />
          <ErrorText value={errors?.periodo_inicio} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="periodo_fim">Período fim</Label>
          <Input id="periodo_fim" type="date" value={inv.periodo_fim} onChange={(e) => form.setData("invoice", { ...inv, periodo_fim: e.target.value })} />
          <ErrorText value={errors?.periodo_fim} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="data_vencimento">Data de vencimento</Label>
          <Input id="data_vencimento" type="date" value={inv.data_vencimento} onChange={(e) => form.setData("invoice", { ...inv, data_vencimento: e.target.value })} />
          <ErrorText value={errors?.data_vencimento} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="valor">Valor</Label>
          <Input id="valor" type="number" step="0.01" value={inv.valor} onChange={(e) => form.setData("invoice", { ...inv, valor: e.target.value })} />
          <ErrorText value={errors?.valor} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="payment_term_id">Condição de pagamento</Label>
          <select
            id="payment_term_id"
            value={inv.payment_term_id}
            onChange={(e) => form.setData("invoice", { ...inv, payment_term_id: e.target.value })}
            className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
          >
            <option value="">Selecione a condição</option>
            {(paymentTerms ?? []).map((pt) => (
              <option key={pt.id} value={pt.id}>
                {pt.label}
              </option>
            ))}
          </select>
          <ErrorText value={errors?.payment_term_id} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="tributos">Tributos</Label>
          <Input
            id="tributos"
            type="text"
            readOnly
            className="border-slate-600 bg-slate-900/80 text-slate-300"
            value={currency(tributosCalculados)}
          />
          <p className="text-xs text-slate-500">
            Calculados automaticamente com base nas alíquotas e retenções da configuração fiscal do cliente para o município
            selecionado (valor × %, alinhado à planilha Base Ductor).
          </p>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="total_da_nota">Total da nota</Label>
          <Input
            id="total_da_nota"
            type="text"
            readOnly
            className="border-slate-600 bg-slate-900/80 font-semibold text-slate-100"
            value={currency(totalDaNota)}
          />
          <p className="text-xs text-slate-500">Soma do valor da nota com o total dos tributos.</p>
        </div>
      </div>

      {inv.company_id && inv.client_id && inv.municipio.trim() && fiscalSetting && (
        <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
          <h3 className="text-sm font-semibold text-slate-200">Serviço e conta Omie (emissão)</h3>
          <p className="mt-1 text-xs text-slate-500">
            Valores sugeridos pela configuração fiscal deste município. Se houver mais de um serviço ou conta sincronizados na
            empresa, escolha abaixo. As descrições vêm do cadastro Omie.
          </p>
          {fiscalSetting.omie_servico_label && (
            <p className="mt-2 text-xs text-slate-400">
              <span className="font-medium text-slate-300">Padrão na config. fiscal (serviço):</span>{" "}
              {fiscalSetting.omie_servico_label}
            </p>
          )}
          {fiscalSetting.omie_conta_label && (
            <p className="text-xs text-slate-400">
              <span className="font-medium text-slate-300">Padrão na config. fiscal (conta):</span>{" "}
              {fiscalSetting.omie_conta_label}
            </p>
          )}
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="omie_servico_id">Serviço Omie (nCodServico)</Label>
              {servicosOmie.length === 0 ? (
                <p className="text-xs text-amber-500/90">Nenhum serviço sincronizado para esta empresa. Execute omie:sync.</p>
              ) : servicosOmie.length === 1 ? (
                <p className="rounded-md border border-slate-800 bg-slate-900/80 px-3 py-2 text-sm text-slate-200">{servicosOmie[0].label}</p>
              ) : (
                <select
                  id="omie_servico_id"
                  value={inv.omie_servico_id}
                  onChange={(e) => form.setData("invoice", { ...inv, omie_servico_id: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
                >
                  <option value="">Selecione o serviço</option>
                  {servicosOmie.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              )}
              <ErrorText value={errors?.omie_servico_id} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="omie_conta_corrente_id">Conta corrente Omie (nCodCC)</Label>
              {contasOmie.length === 0 ? (
                <p className="text-xs text-amber-500/90">Nenhuma conta sincronizada para esta empresa. Execute omie:sync.</p>
              ) : contasOmie.length === 1 ? (
                <p className="rounded-md border border-slate-800 bg-slate-900/80 px-3 py-2 text-sm text-slate-200">{contasOmie[0].label}</p>
              ) : (
                <select
                  id="omie_conta_corrente_id"
                  value={inv.omie_conta_corrente_id}
                  onChange={(e) => form.setData("invoice", { ...inv, omie_conta_corrente_id: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
                >
                  <option value="">Selecione a conta</option>
                  {contasOmie.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              )}
              <ErrorText value={errors?.omie_conta_corrente_id} />
            </div>
          </div>
        </div>
      )}

      {fiscalSetting && (
        <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
          <h3 className="text-sm font-semibold text-slate-200">Tributos por imposto</h3>
          <p className="mt-1 text-xs text-slate-500">
            Valores sobre o valor da nota, conforme a configuração fiscal para o município selecionado.
          </p>
          <div className="mt-3 overflow-x-auto rounded-md border border-slate-800">
            <table className="w-full min-w-[480px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/80 text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-3 py-2 font-medium">Imposto</th>
                  <th className="px-3 py-2 font-medium">Alíquota</th>
                  <th className="px-3 py-2 font-medium">Valor</th>
                  <th className="px-3 py-2 font-medium">Retido</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {tributosBreakdown.linhas.map((linha) => (
                  <tr key={linha.codigo} className="text-slate-200">
                    <td className="px-3 py-2">{linha.nome}</td>
                    <td className="px-3 py-2 tabular-nums text-slate-400">{pct(linha.aliquota_percentual)}</td>
                    <td className="px-3 py-2 tabular-nums font-medium text-slate-100">{currency(linha.valor)}</td>
                    <td className="px-3 py-2 text-xs">
                      {linha.retido ? <span className="text-emerald-400">Sim</span> : <span className="text-slate-500">Não</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-700 bg-slate-900/50 font-medium text-slate-100">
                  <td className="px-3 py-2" colSpan={2}>
                    Total
                  </td>
                  <td className="px-3 py-2 tabular-nums">{currency(tributosBreakdown.total)}</td>
                  <td className="px-3 py-2" />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        <Button asChild type="button" variant="outline" className="border-slate-700 bg-transparent">
          <Link href={initialValues.id ? `/notas-fiscais/${initialValues.id}` : "/notas-fiscais"}>Cancelar</Link>
        </Button>
        <Button type="submit" disabled={form.processing}>
          {form.processing ? "Salvando..." : submitLabel}
        </Button>
      </div>
    </form>
  )
}
